import { type FC, type ReactNode } from "react";

import { clsx } from "clsx";

import { faTriangleExclamation } from "@fortawesome/pro-duotone-svg-icons/faTriangleExclamation";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { SectionLabel } from "./section_label";

// The outline beside its status rail from 1280px, the rail on the right;
// narrower, the rail stacks above the outline. Not sticky: the case header is.
export const IntakeInterviewLayout: FC<{
  rail: ReactNode;
  children: ReactNode;
}> = ({ rail, children }) => (
  <div className="flex flex-col gap-6 xl:flex-row-reverse xl:items-start">
    <aside className="xl:w-[360px] xl:shrink-0">{rail}</aside>
    <div className="min-w-0 flex-1">{children}</div>
  </div>
);

// The outline's parts, stacked.
export const IntakeOutlineParts: FC<{ children: ReactNode }> = ({
  children,
}) => <div className="space-y-4">{children}</div>;

// One part of an intake outline: its label, then its questions.
export const IntakeOutlinePart: FC<{
  label: string;
  children: ReactNode;
}> = ({ label, children }) => (
  <section className="flex flex-col gap-2">
    <SectionLabel size="xs">{label}</SectionLabel>
    <div className="divide-y divide-slate-100 rounded-lg border border-slate-200 bg-white">
      {children}
    </div>
  </section>
);

// One question: the answer in bold, the claimant's own words in grey under
// it. A flagged question is tinted amber, with its flags below.
export const IntakeOutlineEntry: FC<{
  question: string;
  answer?: string;
  words?: string | null;
  flagged: boolean;
  children?: ReactNode;
}> = ({ question, answer, words, flagged, children }) => (
  <div className={clsx("px-3.5 py-2.5", flagged && "bg-amber-50")}>
    <div className="text-xs text-slate-500">{question}</div>
    {answer ? (
      <div className="text-[13px] font-semibold text-slate-900 first-letter:uppercase">
        {answer}
      </div>
    ) : (
      <div className="text-[13px] text-slate-400">Not answered</div>
    )}
    {words && (
      <div className="mt-0.5 text-xs text-slate-500 italic">“{words}”</div>
    )}
    {children}
  </div>
);

// A flag the assistant raised, or a check the server found, in words.
export const IntakeOutlineFlag: FC<{ children: ReactNode }> = ({
  children,
}) => (
  <div className="mt-1 flex items-start gap-1.5 text-xs text-amber-800">
    <FontAwesomeIcon
      icon={faTriangleExclamation}
      className="mt-0.5 shrink-0 text-amber-600"
    />
    <span>{children}</span>
  </div>
);

// One spoken turn: who spoke and when, then what they said.
export const IntakeTranscriptLine: FC<{
  speaker: string;
  at: ReactNode;
  children: ReactNode;
}> = ({ speaker, at, children }) => (
  <div className="flex gap-3 py-1.5 text-[13px]">
    <div className="w-24 shrink-0 text-xs text-slate-400">
      <div className="font-semibold text-slate-500">{speaker}</div>
      {at}
    </div>
    <p className="min-w-0 flex-1 text-slate-700">{children}</p>
  </div>
);
